import { useMemo, useState, type FormEvent } from 'react';
import {
  Bouton, Carte, Dialogue, EnTetePage, Etiquette, Message, Selecteur, Vide,
  ZoneTexte, useRetours,
  ActionsDialogue, IconeTelecharger, IconePlus, SqueletteTableau,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import { useImpression } from '../lib/impression.tsx';

/**
 * Référentiel du programme officiel.
 *
 * ── On masque, on ne supprime pas ───────────────────────────────────────────
 * Le catalogue est global : il est partagé par tous les clients et ne porte
 * aucun `tenant_id`. Retirer un objectif de sa classe ne peut donc pas être une
 * suppression — ce serait le retirer à tout le monde. C'est un masquage propre
 * au client, réversible d'un bouton.
 *
 * L'écran doit le dire, sinon « Retirer » puis « il est revenu chez mon
 * collègue » ressemble à un bug.
 */

interface ObjectifProgramme {
  id: string;
  text: string;
  position: number;
  source: 'programme' | 'client';
}

interface SousDomaineProgramme {
  id: string;
  name: string;
  position: number;
  objectives: ObjectifProgramme[];
}

interface DomaineProgramme {
  id: string;
  name: string;
  subject: string;
  level: string;
  color: string;
  position: number;
  subdomains: SousDomaineProgramme[];
}

interface VersionProgramme {
  id: string;
  label: string;
  cycle: string;
  published: boolean;
  /** Appartient à l'édition choisie dans les réglages de l'école. */
  selected: boolean;
}

/**
 * Version du programme à utiliser par défaut.
 *
 * L'édition choisie par l'école d'abord (`selected`), la plus récemment
 * publiée sinon. Prendre la première de la liste, comme le faisait le code
 * précédent, faisait basculer toutes les écoles le jour d'une nouvelle
 * publication — en pleine année, sans que personne ne l'ait demandé.
 */
export function versionParDefaut(
  versions: Array<{ id: string; selected?: boolean }> | null | undefined,
): string {
  if (!versions || versions.length === 0) return '';
  return (versions.find((v) => v.selected) ?? versions[0])!.id;
}

export function Programme() {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  /*
   * Un document construit, et non `print()` sur l'application : l'aperçu figeait
   * la page appelante tant qu'il restait ouvert (correction reprise de la v1).
   */
  const impression = useImpression({ selecteur: '[data-impression="programme"]', titre: 'Programme' });

  const versions = useDonnees<VersionProgramme[]>('/curriculum/versions');
  const [versionId, setVersionId] = useState('');
  const versionActive = versionId || versionParDefaut(versions.donnees);

  const [classeChoisie, setClasseChoisie] = useState('');
  const arbre = useDonnees<DomaineProgramme[]>(
    versionActive
      ? `/curriculum/${versionActive}/tree`
        + (classeChoisie ? `?level=${encodeURIComponent(classeChoisie)}` : '')
      : null,
    [versionActive, classeChoisie],
  );
  const { executer, enCours, erreur: erreurEcriture } = useEcriture();

  /*
   * Navigation classe → matière, avec fil d'Ariane.
   *
   * Reprend `selectClasse` / `selectMatiere` / `_updateBreadcrumb`. Un cycle
   * porte plusieurs centaines d'objectifs répartis sur six matières : les
   * afficher tous à la suite donne une page qu'on ne parcourt pas, on la
   * subit. Le fil d'Ariane dit où l'on est et permet de remonter d'un cran.
   */
  const reglages = useDonnees<Record<string, string>>('/settings');
  const [matiere, setMatiere] = useState('');
  const classe = classeChoisie;
  const setClasse = setClasseChoisie;

  const classesEcole = (reglages.donnees?.niveaux ?? '')
    .split(',').map((n) => n.trim().toUpperCase()).filter(Boolean);

  const matieresDuCycle = useMemo(
    () => [...new Set((arbre.donnees ?? []).map((d) => d.subject).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'fr')),
    [arbre.donnees],
  );

  /**
   * Domaines affichés.
   *
   * La classe filtre côté SERVEUR, via `levels` : le filtrage des
   * sous-domaines par marqueur y vit déjà, et le refaire ici produirait deux
   * règles concurrentes. Seule la matière est filtrée localement — elle ne
   * demande aucune connaissance du référentiel.
   */
  const domainesAffiches = useMemo(
    () => (arbre.donnees ?? []).filter((d) => !matiere || d.subject === matiere),
    [arbre.donnees, matiere],
  );

  const [ajout, setAjout] = useState<SousDomaineProgramme | null>(null);
  const [texte, setTexte] = useState('');

  async function masquer(objectif: ObjectifProgramme) {
    if (!globalThis.confirm(
      `Retirer « ${objectif.text} » de votre programme ?\n\n`
      + "Il sera seulement masqué pour votre classe : le référentiel officiel "
      + 'reste intact, et vous pourrez le rétablir à tout moment.',
    )) return;
    if (await executer(() =>
      api.requete(`/curriculum/objectives/${objectif.id}/hide`, { methode: 'POST' }))) {
      await arbre.recharger();
    }
  }

  async function toutRetablir() {
    const accepte = await confirmer({
      titre: 'Rétablir tous les objectifs masqués ?',
      message: 'Votre programme retrouvera le contenu officiel complet.',
      libelleConfirmation: 'Tout rétablir',
    });
    if (!accepte) return;
    const ok = await executer(async () => {
      const r = await api.requete<{ restored: number }>('/curriculum/reset', {
        methode: 'POST',
      });
      notifier(
        r.restored > 0
          ? `${r.restored} objectif(s) rétabli(s).`
          : 'Aucun objectif n\'était masqué.',
      );
    });
    if (ok) await arbre.recharger();
  }

  async function ajouter(evenement: FormEvent) {
    evenement.preventDefault();
    if (!ajout) return;
    const ok = await executer(() =>
      api.requete('/curriculum/objectives', {
        methode: 'POST',
        corps: {
          subdomainId: ajout.id,
          text: texte.trim(),
          position: ajout.objectives.length,
        },
      }));
    if (ok) {
      setAjout(null);
      setTexte('');
      await arbre.recharger();
    }
  }

  if (versions.chargement && !versions.donnees) {
    return (
      <div className="flex flex-col gap-5">
        <EnTetePage titre="Programme" />
        <Carte><SqueletteTableau lignes={6} colonnes={2} /></Carte>
      </div>
    );
  }
  if (versions.erreur) return <Message ton="erreur">{versions.erreur}</Message>;

  if ((versions.donnees?.length ?? 0) === 0) {
    return (
      <Carte titre="Programme">
        <Vide titre="Aucun programme disponible">
          Le référentiel officiel n'a pas encore été chargé sur cette
          installation.
        </Vide>
      </Carte>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-impression="programme">
      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}
      {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}

      <EnTetePage
        titre="Programme"
        contexte={
          versions.donnees?.find((v) => v.id === versionActive)
            ? `${versions.donnees.find((v) => v.id === versionActive)!.label}`
              + ` · ${versions.donnees.find((v) => v.id === versionActive)!.cycle}`
            : undefined
        }
        actions={
          <Bouton
            variante="ghost"
            icone={<IconeTelecharger taille={16} />}
            chargement={impression.enCours}
            onClick={impression.lancer}
          >
            Télécharger le PDF
          </Bouton>
        }
      />

      <Carte
        titre="Votre programme"
        action={
          <div className="flex flex-wrap items-end gap-2">
            {/*
              * Un cycle ne se choisit que si l'école en couvre plusieurs — une
              * classe de CM1-CM2 tient dans le cycle 3, une école qui va de la
              * maternelle au CM2 en couvre trois. Le sélecteur d'ÉDITION, lui,
              * a disparu : il vit dans les réglages, où on le règle une fois.
              */}
            {(versions.donnees?.length ?? 0) > 1 && (
              <Selecteur
                libelle="Cycle"
                value={versionActive}
                onChange={(e) => setVersionId(e.target.value)}
                options={(versions.donnees ?? []).map((v) => ({
                  valeur: v.id, texte: v.cycle,
                }))}
              />
            )}
            {classesEcole.length > 1 && (
              <Selecteur
                libelle="Classe"
                value={classe}
                onChange={(e) => { setClasse(e.target.value); setMatiere(''); }}
                options={[
                  { valeur: '', texte: 'Toutes mes classes' },
                  ...classesEcole.map((n) => ({ valeur: n, texte: n })),
                ]}
              />
            )}
            {matieresDuCycle.length > 1 && (
              <Selecteur
                libelle="Matière"
                value={matiere}
                onChange={(e) => setMatiere(e.target.value)}
                options={[
                  { valeur: '', texte: 'Toutes les matières' },
                  ...matieresDuCycle.map((m) => ({ valeur: m, texte: m })),
                ]}
              />
            )}
            <Bouton variante="secondary" onClick={() => void toutRetablir()}>
              Tout rétablir
            </Bouton>
          </div>
        }
      >
        {/*
          * Fil d'Ariane. Chaque cran est un bouton qui remonte : c'est le seul
          * moyen de revenir en arrière sans chercher l'option « toutes » dans
          * une liste déroulante.
          */}
        <nav aria-label="Position dans le programme" className="mb-3">
          <ol className="flex flex-wrap items-center gap-1.5 text-[13px]">
            <li className="font-semibold text-[var(--color-primary)]">
              {versions.donnees?.find((v) => v.id === versionActive)?.cycle ?? 'Programme'}
            </li>
            {classe && (
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true" className="text-[var(--color-fg-muted)]">›</span>
                <button
                  type="button"
                  onClick={() => { setClasse(''); setMatiere(''); }}
                  className="rounded font-medium underline underline-offset-2 hover:text-[var(--color-primary)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                >
                  {classe}
                </button>
              </li>
            )}
            {matiere && (
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true" className="text-[var(--color-fg-muted)]">›</span>
                <button
                  type="button"
                  onClick={() => setMatiere('')}
                  className="rounded underline underline-offset-2 hover:text-[var(--color-primary)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
                >
                  {matiere}
                </button>
              </li>
            )}
          </ol>
        </nav>

        <Message>
          Retirer un objectif le masque pour votre classe uniquement. Le
          référentiel officiel n'est jamais modifié, et « Tout rétablir » ramène
          l'ensemble.
        </Message>
      </Carte>

      {arbre.chargement && !arbre.donnees && <Message>Chargement du programme…</Message>}

      {domainesAffiches.map((domaine) => (
        <Carte
          key={domaine.id}
          titre={`${domaine.name} · ${domaine.subject} (${domaine.level})`}
        >
          <div className="flex flex-col gap-4">
            {domaine.subdomains.map((sousDomaine) => (
              <section key={sousDomaine.id} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">{sousDomaine.name}</h3>
                  <Bouton
                    variante="ghost"
                    taille="sm"
                    icone={<IconePlus taille={15} />}
                    onClick={() => { setAjout(sousDomaine); setTexte(''); }}
                    aria-label={`Ajouter un objectif dans ${sousDomaine.name}`}
                  >
                    Ajouter un objectif
                  </Bouton>
                </div>
                {sousDomaine.objectives.length === 0 ? (
                  <p className="text-sm text-[var(--color-fg-muted)]">
                    Aucun objectif, tous ont été masqués pour votre classe.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {sousDomaine.objectives.map((objectif) => (
                      <li
                        key={objectif.id}
                        className="flex flex-wrap items-center justify-between gap-2 text-sm"
                      >
                        <span className="flex max-w-xl items-center gap-2">
                          <span>{objectif.text}</span>
                          {objectif.source === 'client' && (
                            <Etiquette>Ajouté par vous</Etiquette>
                          )}
                        </span>
                        {objectif.source === 'programme' && (
                          <Bouton
                            variante="ghost"
                            onClick={() => void masquer(objectif)}
                            aria-label={`Retirer l'objectif ${objectif.text}`}
                          >
                            Retirer
                          </Bouton>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </Carte>
      ))}

      <Dialogue
        titre={ajout ? `Nouvel objectif dans ${ajout.name}` : 'Nouvel objectif'}
        ouvert={ajout !== null}
        surFermeture={() => setAjout(null)}
      >
        <form onSubmit={ajouter} className="flex flex-col gap-4">
          <ZoneTexte
            libelle="Objectif"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            aide="Il s'ajoute à votre programme seulement, et reste évaluable comme les autres."
            autoFocus
          />
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setAjout(null)}>
              Annuler
            </Bouton>
            <Bouton type="submit" chargement={enCours} disabled={!texte.trim()}>
              Ajouter
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>
    </div>
  );
}
